import React from "react";
import { siteData } from "../data/mock";
import { Header } from "../components/landing/Header";
import { ProgramSections } from "../components/landing/ProgramSections";
import { BottomSections } from "../components/landing/BottomSections";
import {
  ArrowDown,
  Check,
  X,
  ShieldOff,
  Flame,
  ArrowRight,
} from "lucide-react";

const HeroSection = () => {
  const { hero, images } = siteData;
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with Marcus's photo */}
      <div className="absolute inset-0">
        <img
          src={images.marcusAfterMain.url}
          alt={images.marcusAfterMain.alt}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      </div>

      {/* Grain texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%270 0 256 256%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cfilter id=%27noise%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%274%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23noise)%27/%3E%3C/svg%3E")', backgroundRepeat: 'repeat' }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-32 pb-24">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7dfabf]/30 bg-[#7dfabf]/5 mb-10 animate-fadeIn">
          <Flame size={16} className="text-[#7dfabf]" />
          <span className="text-sm text-[#7dfabf] font-medium tracking-wider uppercase font-body">
            16-Week Transformation Program
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-8xl font-black text-white leading-[0.95] mb-6 sm:mb-8 tracking-tight animate-slideUp font-heading">
          {hero.headline}
        </h1>

        <p className="text-lg sm:text-xl text-[#7dfabf] font-semibold mb-6 animate-slideUp animation-delay-200 font-body">
          {hero.subheadline}
        </p>

        <p className="text-lg text-white/60 max-w-2xl mx-auto mb-12 leading-relaxed animate-slideUp animation-delay-400 font-body">
          {hero.description}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slideUp animation-delay-600">
          <button
            onClick={() => document.querySelector("#apply")?.scrollIntoView({ behavior: "smooth" })}
            className="group px-10 py-4 bg-[#7dfabf] text-black font-bold text-lg rounded-lg hover:bg-[#6ae0a8] transition-all duration-300 hover:shadow-xl hover:shadow-[#7dfabf]/20 hover:-translate-y-0.5 font-body flex items-center gap-2"
          >
            {hero.cta}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <button
            onClick={() => document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" })}
            className="px-10 py-4 border border-white/20 text-white font-semibold text-lg rounded-lg hover:border-[#7dfabf]/50 hover:text-[#7dfabf] transition-all duration-300 font-body"
          >
            {hero.secondaryCta}
          </button>
        </div>

        <div className="mt-20 animate-bounce">
          <ArrowDown size={24} className="text-[#7dfabf]/60 mx-auto" />
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  const { about, images } = siteData;
  return (
    <section id="about" className="py-28 lg:py-36 relative">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-8 tracking-tight font-heading">
              {about.title}
            </h2>
            <p className="text-lg text-white/60 mb-8 leading-relaxed font-body">
              {about.description}
            </p>
            <div className="p-6 rounded-2xl bg-[#7dfabf]/5 border border-[#7dfabf]/20">
              <p className="text-xl lg:text-2xl text-[#7dfabf] font-semibold leading-relaxed font-body">
                {about.highlight}
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden bg-[#111]">
              <img
                src={images.marcusCafe.url}
                alt={images.marcusCafe.alt}
                className="w-full aspect-[3/4] object-cover object-center rounded-2xl"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#7dfabf]/20 rounded-2xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

const WhoForSection = () => {
  const { whoFor } = siteData;
  return (
    <section className="py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-4xl lg:text-5xl font-black text-white text-center mb-16 tracking-tight font-heading">
          {whoFor.title}
        </h2>
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          <div className="p-8 lg:p-10 rounded-2xl bg-[#0A0A0A] border border-[#1E3A2A] hover:border-[#7dfabf]/20 transition-colors duration-500">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-[#7dfabf]/10 flex items-center justify-center">
                <Check size={20} className="text-[#7dfabf]" />
              </div>
              <h3 className="text-xl font-bold text-[#7dfabf] font-heading">This IS for you</h3>
            </div>
            <ul className="space-y-5">
              {whoFor.isFor.map((item, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-[#7dfabf]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#7dfabf]/20 transition-colors duration-300">
                    <Check size={14} className="text-[#7dfabf]" />
                  </div>
                  <span className="text-white/80 leading-relaxed font-body">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 lg:p-10 rounded-2xl bg-[#0A0A0A] border border-[#3A1E1E] hover:border-[#EF4444]/20 transition-colors duration-500">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                <X size={20} className="text-[#EF4444]" />
              </div>
              <h3 className="text-xl font-bold text-[#EF4444] font-heading">This is NOT for you</h3>
            </div>
            <ul className="space-y-5">
              {whoFor.isNotFor.map((item, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#EF4444]/20 transition-colors duration-300">
                    <X size={14} className="text-[#EF4444]" />
                  </div>
                  <span className="text-white/80 leading-relaxed font-body">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-center text-lg text-[#7dfabf] font-semibold mt-12 font-body">
          {whoFor.bottomLine}
        </p>
      </div>
    </section>
  );
};

const RealProblemSection = () => {
  const { realProblem, images } = siteData;
  return (
    <section className="py-28 lg:py-36 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight font-heading">
              {realProblem.title}
            </h2>
            <p className="text-2xl text-white font-semibold mb-4 font-heading">
              {realProblem.intro}
            </p>
            <p className="text-lg text-white/60 mb-8 font-body">
              {realProblem.description}
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              {realProblem.points.map((point, i) => (
                <div key={i} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0A0A0A] border border-white/10">
                  <ShieldOff size={18} className="text-[#7dfabf]" />
                  <span className="text-white/80 text-lg font-body">{point}</span>
                </div>
              ))}
            </div>
            <div className="p-6 rounded-2xl bg-[#7dfabf]/5 border border-[#7dfabf]/20">
              <p className="text-xl text-[#7dfabf] font-semibold leading-relaxed font-body">
                {realProblem.solution}
              </p>
            </div>
          </div>
          <div className="flex justify-center">
            <img
              src={images.reliefQuote.url}
              alt={images.reliefQuote.alt}
              className="w-full max-w-md rounded-2xl shadow-2xl shadow-[#7dfabf]/5"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const TransformationSection = () => {
  const { transformation } = siteData;
  return (
    <section className="py-20 sm:py-28 lg:py-36 relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#7dfabf]/30 to-transparent" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight font-heading">
            {transformation.title}
          </h2>
          <p className="text-base sm:text-lg text-white/60 font-body">
            {transformation.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Before */}
          <div>
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-3 h-3 rounded-full bg-[#EF4444]" />
              <h3 className="text-xl sm:text-2xl font-bold text-[#EF4444] font-heading">
                {transformation.before.label}
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="col-span-2 aspect-[4/3] rounded-xl overflow-hidden bg-[#111]">
                <img
                  src={transformation.before.images[0].url}
                  alt={transformation.before.images[0].alt}
                  className="w-full h-full object-cover object-center rounded-xl"
                  loading="lazy"
                />
              </div>
              <div className="aspect-square rounded-xl overflow-hidden bg-[#111]">
                <img
                  src={transformation.before.images[1].url}
                  alt={transformation.before.images[1].alt}
                  className="w-full h-full object-cover object-center rounded-xl"
                  loading="lazy"
                />
              </div>
              <div className="aspect-square rounded-xl overflow-hidden bg-[#111]">
                <img
                  src={transformation.before.images[2].url}
                  alt={transformation.before.images[2].alt}
                  className="w-full h-full object-cover object-center rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* After */}
          <div>
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-3 h-3 rounded-full bg-[#7dfabf]" />
              <h3 className="text-xl sm:text-2xl font-bold text-[#7dfabf] font-heading">
                {transformation.after.label}
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="col-span-2 aspect-[4/3] rounded-xl overflow-hidden bg-[#111]">
                <img
                  src={transformation.after.images[0].url}
                  alt={transformation.after.images[0].alt}
                  className="w-full h-full object-cover object-top rounded-xl"
                  loading="lazy"
                />
              </div>
              <div className="aspect-square rounded-xl overflow-hidden bg-[#111]">
                <img
                  src={transformation.after.images[1].url}
                  alt={transformation.after.images[1].alt}
                  className="w-full h-full object-cover object-center rounded-xl"
                  loading="lazy"
                />
              </div>
              <div className="aspect-square rounded-xl overflow-hidden bg-[#111]">
                <img
                  src={transformation.after.images[2].url}
                  alt={transformation.after.images[2].alt}
                  className="w-full h-full object-cover object-center rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function LandingPage() {
  return (
    <main className="bg-black min-h-screen overflow-x-hidden">
      <Header />
      <HeroSection />
      <AboutSection />
      <WhoForSection />
      <RealProblemSection />
      <TransformationSection />
      <ProgramSections />
      <BottomSections />
    </main>
  );
}
